import { Navigate, Outlet } from "react-router-dom";

import { landingPathFor } from "@/lib/landing";
import { usePermissions, type PermissionFlags } from "@/hooks/usePermissions";

interface RequirePermissionProps {
  /** Permission the wrapped routes require. */
  permission: keyof PermissionFlags;
}

/**
 * Route-level permission gate.
 *
 * Until now the app had none: ``AuthGate`` proves you are signed in, and
 * that was the only barrier in the router. ``/admin`` was reachable by
 * typing it — the page rendered and every API call behind it 403'd, which
 * is safe but looks broken.
 *
 * That was tolerable while every role was clinical. With ``TESTRAD`` it
 * is not: a candidate who wanders onto a clinical route should be
 * returned to their own landing page, not shown a shell of the clinic's
 * software.
 *
 * This is UX, not security. The backend dependency is the real gate —
 * see ``require_permission`` and ``require_clinical_access``.
 */
export function RequirePermission({ permission }: RequirePermissionProps) {
  const perms = usePermissions();

  // A null role means the session is still being restored, NOT that the
  // user is unauthorised.
  //
  // AuthGate sets the access token and the user in two separate store
  // writes, so there is a render between them where the token exists and
  // the role does not. Redirecting on that render sent a candidate who
  // refreshed mid-exam to /worklist (the null-role landing), which the
  // clinical gate then bounced to /tests — so a page refresh silently
  // threw them out of the attempt they were sitting.
  //
  // Rendering nothing for that one frame is correct: AuthGate is already
  // showing its own loading state around us.
  if (perms.role === null) return null;

  if (!perms.has(permission)) {
    return <Navigate to={landingPathFor(perms.role)} replace />;
  }
  return <Outlet />;
}
